import React, { useEffect, useState } from "react";
import { useLanguage, useT } from "@/lib/LanguageContext";
import * as crmApi from "../../api/crmApi";
import {
  DELIVERY_KEYS, SOURCE_KEYS, EMAIL_STATUS_KEYS, FEED_PAGE_SIZE, INTERACTION_KEYS, STAGE_KEYS, TIMELINE_CATEGORY_KEYS,
  TIMELINE_FILTERS, TIMELINE_FILTER_KEYS, TIMELINE_KIND_KEYS,
} from "../../contract";
import { fmtRelative } from "../../format";
import useClientTab from "../../hooks/useClientTab";
import useCrmMoney from "../../hooks/useCrmMoney";
import useNow from "../../hooks/useNow";
import FilterChips from "../../ui/FilterChips";
import TimelineFeed from "../../ui/TimelineFeed";
import StatusBadge, { emailStatusTone } from "../../ui/StatusBadge";
import EmptyState from "../../ui/EmptyState";
import CrmLoadFailure from "../../ui/CrmLoadFailure";
import CrmButton from "../../ui/CrmButton";
import { CRM } from "../../../../constants/testIds/crm";

/** Dot colour per category in the product palette: session sage, email ochre, purchase ink, notes muted, other border grey. */
const DOT = { session: "bg-eh-sage-deep", email: "bg-eh-ochre", purchase: "bg-eh-ink", note: "bg-eh-muted", other: "bg-eh-border" };

/**
 * Timeline (default tab): the chronological feed — emails with opened/bounced status,
 * purchases, sessions, pack events, interactions, stage changes — filtered by the chips
 * All · Emails · Purchases · Sessions · Notes, newest first, "Load more" at the end.
 *
 * Each row's sentence is composed HERE from `kind` + `data` (C0.06); an unknown kind
 * falls back to its category's title.
 */
export default function TimelineTab({ clientId }) {
  const t = useT("crm");
  const { lang } = useLanguage();
  const money = useCrmMoney();
  const now = useNow(60000);
  const [category, setCategory] = useState("all");
  const params = category === "all" ? {} : { category };
  const list = useClientTab(clientId, crmApi.listTimeline, params, FEED_PAGE_SIZE);
  const [rows, setRows] = useState([]);

  // Pages accumulate for "Load more"; a new filter starts over.
  useEffect(() => { setRows([]); }, [category]);
  useEffect(() => {
    if (!list.loaded) return;
    setRows((prev) => (list.page === 1 ? list.rows : [...prev, ...list.rows.filter((r) => !prev.some((p) => p.id === r.id))]));
  }, [list.rows, list.page, list.loaded]);

  const detail = (row) => {
    const d = row.data || {};
    const parts = [];
    if (row.kind === "stage_changed" && STAGE_KEYS[d.from] && STAGE_KEYS[d.to]) {
      parts.push(`${t(STAGE_KEYS[d.from])} → ${t(STAGE_KEYS[d.to])}`);
    }
    if (d.service_name) parts.push(d.service_name);
    if (d.delivery_mode && DELIVERY_KEYS[d.delivery_mode]) parts.push(t(DELIVERY_KEYS[d.delivery_mode]));
    if (d.pack_total) parts.push(t("timeline.packPosition", { used: d.pack_used, total: d.pack_total }));
    if (d.note_added) parts.push(t("timeline.noteAdded"));
    if (d.subject) parts.push(d.subject);
    if (d.item_name) parts.push(d.item_name);
    if (d.amount_cents !== undefined && d.amount_cents !== null) parts.push(money(d.amount_cents, d.currency));
    if (d.type && INTERACTION_KEYS[d.type] && !String(row.kind).startsWith("interaction_")) parts.push(t(INTERACTION_KEYS[d.type]));
    if (d.summary) parts.push(d.summary);
    if (row.kind === "legacy" && d.legacy_text) parts.push(d.legacy_text);
    if (d.title) parts.push(d.title);
    if (d.level_title) parts.push(d.level_title);
    if (d.step_title) parts.push(d.step_title);
    if (d.filename) parts.push(d.filename);
    if (d.source && SOURCE_KEYS[d.source]) parts.push(t(SOURCE_KEYS[d.source]));
    return parts.join(" · ");
  };

  const title = (row) => {
    if (row.kind === "stage_changed") return row.data?.mode === "manual" ? t("timeline.stageManual") : t("timeline.stageAuto");
    const key = TIMELINE_KIND_KEYS[row.kind] || TIMELINE_CATEGORY_KEYS[row.category] || TIMELINE_CATEGORY_KEYS.other;
    return t(key);
  };

  const items = rows.map((row) => {
    const status = row.email_status || row.data?.status;
    const tone = status && emailStatusTone(status);
    return {
      id: row.id,
      testId: CRM.clientTimelineItem(row.id),
      dotClass: DOT[row.category] || DOT.other,
      title: title(row),
      detail: detail(row),
      // The visitor's own words: shown whole, not squeezed into the detail line.
      body: row.kind === "form_submitted" ? (row.data?.message || "").trim() : "",
      badge: status && EMAIL_STATUS_KEYS[status] && tone ? <StatusBadge tone={tone}>{t(EMAIL_STATUS_KEYS[status])}</StatusBadge> : null,
      at: row.occurred_at,
      when: fmtRelative(row.occurred_at, now, lang),
    };
  });

  return (
    <div className="space-y-3">
      <FilterChips label={t("timeline.filterLabel")} value={category} onChange={setCategory}
        options={TIMELINE_FILTERS.map((k) => ({ key: k, label: t(TIMELINE_FILTER_KEYS[k]), testId: CRM.clientTimelineFilter(k) }))} />
      {list.loadError && <CrmLoadFailure error={list.loadError} onRetry={list.reload} />}
      {list.loaded && !list.loadError && rows.length === 0 && <EmptyState title={t("timeline.empty")} />}
      {items.length > 0 && <TimelineFeed items={items} />}
      {rows.length < list.total && (
        <CrmButton variant="outline" size="sm" disabled={list.loading} onClick={() => list.setPage(list.page + 1)} data-testid={CRM.clientTimelineMore}>
          {t("common.loadMore")}
        </CrmButton>
      )}
    </div>
  );
}
